import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown, Toast, ToastContainer } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { getLoanTasks } from '../../services/loanService';
import type { BpmTask } from '../../services/schemas/loanServiceSchemas/bpmTask';

/** Each workflow step as the team calls it. */
export const STEP_LABELS: Record<string, string> = {
  PROCESS_LEAD: 'Process lead',
  CAPTURE_APPLICATION: 'Capture application',
  UPLOAD_DOCUMENTS: 'Upload documents',
  VERIFY_DOCUMENTS: 'Verification',
  VERIFY_COLLATERAL: 'Collateral verification',
  CREDIT_ASSESSMENT: 'Credit assessment',
  APPROVE_LOAN: 'Credit Committee',
  REVIEW_APPLICATION: 'Review application',
  UPLOAD_AGREEMENT: 'Loan agreement',
  DISBURSE_FUNDS: 'Disbursement',
};

/** The order a file moves through the steps, so the list reads like the process. */
const STEP_ORDER = Object.keys(STEP_LABELS);

export const stepLabel = (type?: string): string =>
  (type && STEP_LABELS[type]) || (type || '').replace(/_/g, ' ').toLowerCase();

const customerOf = (task: BpmTask): string =>
  task.company?.companyName || task.fullName || task.customerNumber || 'Client';

const daysWaiting = (iso?: string): number =>
  iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)) : 0;

const waitedFor = (iso?: string): string => {
  const days = daysWaiting(iso);
  return days === 0 ? 'today' : days === 1 ? '1 day' : `${days} days`;
};

export interface StepWaiting {
  type: string;
  count: number;
  oldest?: string;
}

/** Unclaimed work grouped by step, in process order. */
export const groupByStep = (tasks: BpmTask[]): StepWaiting[] => {
  const byStep = new Map<string, StepWaiting>();
  for (const task of tasks) {
    const type = task.workItemType || 'OTHER';
    const step = byStep.get(type) || { type, count: 0 };
    step.count++;
    if (task.createdDate && (!step.oldest || task.createdDate < step.oldest)) step.oldest = task.createdDate;
    byStep.set(type, step);
  }
  const rank = (t: string) => (STEP_ORDER.indexOf(t) === -1 ? 99 : STEP_ORDER.indexOf(t));
  return Array.from(byStep.values()).sort((a, b) => rank(a.type) - rank(b.type));
};

const POLL_MS = 2 * 60_000;
const PAGE_SIZE = 200;

const shownKey = (user: string, day: string) => `tirhelo.workWaitingReminder.${user}.${day}`;
const wasShown = (key: string): boolean => {
  try { return window.localStorage.getItem(key) === '1'; } catch { return false; }
};
const markShown = (key: string) => {
  try { window.localStorage.setItem(key, '1'); } catch { /* the summary may show again; harmless */ }
};

/**
 * US-49: work waiting for the user, in the header. The badge counts unclaimed work at the steps the user's
 * groups handle plus work they have claimed; the list shows each step with how long the oldest has waited;
 * a notice appears when new work arrives while the app is open, and a summary the first time each day.
 * Until roles are decided everyone is an administrator, so everyone sees every step.
 */
const WorkWaitingAlert: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const userEmail = user?.email;
  const userGroups = useMemo(() => user?.userGroups || [], [user?.userGroups]);
  const isAdmin = userGroups.some(group => group === 'ADMIN');

  const [waiting, setWaiting] = useState<BpmTask[]>([]);
  const [mine, setMine] = useState<BpmTask[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [newWork, setNewWork] = useState<BpmTask[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const seen = useRef<Set<string> | null>(null);

  const load = useCallback(async () => {
    if (!userGroups.length && !userEmail) return;
    try {
      const groups = isAdmin ? ['ADMIN'] : userGroups;
      const [groupPages, minePage] = await Promise.all([
        Promise.all(groups.map(group => getLoanTasks({ assigneeGroup: group, page: 0, size: PAGE_SIZE }))),
        userEmail ? getLoanTasks({ assignee: userEmail, page: 0, size: PAGE_SIZE }) : Promise.resolve(null),
      ]);
      const unclaimed = Array.from(new Map(
        groupPages.flatMap(page => page?.content || []).filter(t => !t.assignee).map(t => [t.taskId, t])
      ).values());
      const claimed = minePage?.content || [];

      // New since the last look: work that was not waiting before (the first load only sets the baseline)
      const ids = new Set(unclaimed.map(t => t.taskId));
      if (seen.current) {
        const arrived = unclaimed.filter(t => !seen.current!.has(t.taskId));
        if (arrived.length) setNewWork(arrived);
      }
      seen.current = ids;

      setWaiting(unclaimed);
      setMine(claimed);
      setLoaded(true);
    } catch (error) {
      // The header must not break when the workflow service is unavailable
      console.error('Loading waiting work failed:', error);
    }
  }, [isAdmin, userGroups, userEmail]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const steps = useMemo(() => groupByStep(waiting), [waiting]);
  const total = waiting.length + mine.length;
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    if (!loaded || total === 0) return;
    const key = shownKey(userEmail || user?.name || 'user', today);
    if (!wasShown(key)) {
      markShown(key);
      setShowSummary(true);
    }
  }, [loaded, total, userEmail, user?.name, today]);

  if (!user) return null;

  const summary = total
    ? `${waiting.length} item${waiting.length === 1 ? '' : 's'} waiting to be picked up${mine.length ? `, ${mine.length} claimed by you` : ''}`
    : 'No work waiting';
  const stepLine = steps.map(s => `${s.count} ${stepLabel(s.type).toLowerCase()}`).join(', ');

  return (
    <>
      <li>
        <Dropdown align="end">
          <Dropdown.Toggle
            as="a"
            href="#"
            bsPrefix="nav-link"
            className="position-relative px-2 text-secondary"
            title={summary}
            aria-label={summary}
          >
            <i className="fa fa-inbox fs-5"></i>
            {total > 0 && (
              <span className="badge rounded-pill bg-primary position-absolute top-0 start-100 translate-middle" style={{ fontSize: 11 }}>
                {total}
              </span>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu className="border-0 rounded-4 shadow p-0" style={{ width: 340 }}>
            <div className="px-3 py-2 border-bottom">
              <div className="fw-semibold">Work waiting</div>
              <small className="text-muted">{summary}</small>
            </div>

            {steps.map(step => (
              <Dropdown.Item
                key={step.type}
                as={Link}
                to={`/work-items?stage=${step.type}`}
                className="d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <span className="fw-semibold">{stepLabel(step.type)}</span>
                <span className="text-end ms-2">
                  <span className="badge bg-primary">{step.count}</span>
                  <small className={`d-block ${daysWaiting(step.oldest) >= 2 ? 'text-danger' : 'text-muted'}`}>
                    oldest {waitedFor(step.oldest)}
                  </small>
                </span>
              </Dropdown.Item>
            ))}

            {mine.length > 0 && (
              <Dropdown.Item as={Link} to="/work-items?tab=my" className="d-flex justify-content-between py-2 border-bottom">
                <span>Claimed by you</span>
                <span className="badge bg-secondary">{mine.length}</span>
              </Dropdown.Item>
            )}

            <Dropdown.Item as={Link} to="/work-items" className="text-center text-primary py-2">
              Open Work Items
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </li>

      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1081, position: 'fixed' }}>
        <Toast show={newWork.length > 0} onClose={() => setNewWork([])} delay={20000} autohide>
          <Toast.Header>
            <i className="fa fa-inbox me-2 text-primary"></i>
            <strong className="me-auto">New work waiting</strong>
          </Toast.Header>
          <Toast.Body>
            {newWork.slice(0, 3).map(task => (
              <div key={task.taskId} className="mb-1">
                <strong>{stepLabel(task.workItemType)}</strong>: {customerOf(task)}
              </div>
            ))}
            {newWork.length > 3 && <div className="text-muted small mb-1">and {newWork.length - 3} more</div>}
            <button
              type="button"
              className="btn btn-sm btn-primary mt-1"
              onClick={() => {
                const first = newWork[0];
                setNewWork([]);
                navigate(newWork.length === 1 && first ? `/work-items?stage=${first.workItemType}` : '/work-items');
              }}
            >
              Open Work Items
            </button>
          </Toast.Body>
        </Toast>

        <Toast show={showSummary && newWork.length === 0} onClose={() => setShowSummary(false)} delay={15000} autohide>
          <Toast.Header>
            <i className="fa fa-inbox me-2 text-primary"></i>
            <strong className="me-auto">Work waiting today</strong>
          </Toast.Header>
          <Toast.Body>
            <div className="mb-2">{summary}{stepLine ? `: ${stepLine}` : ''}.</div>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => { setShowSummary(false); navigate('/work-items'); }}>
              Open Work Items
            </button>
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default WorkWaitingAlert;
